import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  adminGetVariantsByProduct,
  adminDeleteVariant,
  adminGetAllProducts,
  adminGetProductById
} from "../api/admin";
import VariantForm from "../components/VariantForm";
import VariantTable from "../components/VariantTable";
import "./VariantPage.css";
import { toast } from "react-toastify";

const VariantPage = ({ productId: initialProductId }) => {
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(initialProductId || "");
  const [variants, setVariants] = useState([]);
  const [, setProductImages] = useState([]);
  const [productUrl, setProductUrl] = useState("");
  const [productName, setProductName] = useState("");
  const [groupedProducts, setGroupedProducts] = useState([]);
  const [editingVariant, setEditingVariant] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [variantsLoading, setVariantsLoading] = useState(false);
  
  const role = localStorage.getItem("adminRole");
  const permissions = JSON.parse(localStorage.getItem("adminPermissions") || "[]");

  const canWrite = role === "admin" || permissions.includes("Variants:write");
  const location = useLocation();
  const navigate = useNavigate();
  const [isAutoEditing, setIsAutoEditing] = useState(!!location.state?.highlightVariantId);

  // Handle automatic variant editing from dashboard
  useEffect(() => {
    // If we've finished loading and still haven't found the variant, or if loading failed
    if (!variantsLoading && isAutoEditing && location.state?.highlightVariantId) {
      const variantToEdit = variants.find(v => (v._id || v.id) === location.state.highlightVariantId);
      if (variantToEdit) {
        setEditingVariant(variantToEdit);
        setShowForm(true);
        setIsAutoEditing(false);
        window.history.replaceState({}, document.title);
      } else if (variants.length > 0 || !variantsLoading) {
        // Stop auto-editing if variants arrived but no match, or if fetch finished with nothing
        setIsAutoEditing(false);
      }
    }
  }, [variants, isAutoEditing, location.state, variantsLoading]);

  useEffect(() => {
    if (initialProductId) {
      setSelectedProductId(initialProductId);
    }
  }, [initialProductId]);

  // Fetch all products for dropdown
  const fetchProducts = async () => {
    try {
      const res = await adminGetAllProducts();
      setProducts(res.data?.products || res.data || []);
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  // Fetch variants for selected product
  const fetchVariants = async (productId) => {
    if (!productId) return;
    try {
      setVariantsLoading(true);
      let variantList = [];
      let images = [];
      let grouped = [];
      console.log("DEBUG: Starting fetchVariants for productId:", productId);

      // 1️⃣ Try product details first (contains enriched variants and images)
      try {
        const prodRes = await adminGetProductById(productId);
        const prodData = prodRes.data;
        console.log("DEBUG: adminGetProductById raw prodData:", prodData);

        const prod = prodData?.product || prodData?.data || (prodData?.variants ? prodData : null);

        if (prod) {
          variantList = prod.variants || [];
          images = prod.p_images || [];
          setProductUrl(prod?.p_url || "");
          setProductName(prod?.p_name || "");
          grouped = prodData?.groupedProducts || [];
        }
      } catch (e) {
        console.warn("Product details fetch failed, trying variants endpoint...", e);
      }

      // 2️⃣ FALLBACK: Try dedicated variants endpoint if still empty
      if (!variantList || variantList.length === 0) {
        try {
          const res = await adminGetVariantsByProduct(productId);
          const data = res.data;
          
          variantList = Array.isArray(data) ? data : (data?.variants || data?.data?.variants || []);
          if (data?.groupedProducts) grouped = data.groupedProducts;
          if (data?.product) {
            images = data.product.p_images || [];
            setProductUrl(data.product.p_url || "");
            setProductName(data.product.p_name || "");
          }
        } catch (e) {
          console.error("Variants endpoint fetch failed:", e);
        }
      }

      setVariants(variantList || []);
      setProductImages(images || []);
      setGroupedProducts(grouped || []);
    } catch (err) {
      console.error("Critical error in fetchVariants:", err);
      setVariants([]);
    } finally {
      setVariantsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Fetch variants whenever selected product changes
  useEffect(() => {
    if (selectedProductId) {
      fetchVariants(selectedProductId);
      setEditingVariant(null); // reset edit if product changes
    } else {
      setVariants([]);
    }
  }, [selectedProductId]);

  // Delete variant
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this variant?")) return;
    try {
      await adminDeleteVariant(id);
      fetchVariants(selectedProductId);
      toast.success("Variant deleted successfully! 🗑️");
    } catch (error) {
      console.error("Error deleting variant:", error);
      toast.error(error.response?.data?.message || "Failed to delete variant. ❌");
    }
  };

  // Edit variant
  const handleEdit = (variant) => {
    setEditingVariant(variant);
    setShowForm(true);
  };

  const cancelEdit = () => {
    if (location.state?.fromDashboard) {
      navigate("/adminDashboard");
    } else {
      setEditingVariant(null);
      setShowForm(false);
    }
  };

  if (isAutoEditing && !showForm) {
    return (
      <div className="admin-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Redirecting to variant details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {!showForm ? (
        <>
          <div className="page-header">
            <div className="header-with-back">
              <button 
                className="back-btn-small" 
                onClick={() => navigate("/adminDashboard/products")}
                title="Back to products"
              >
                ← Back
              </button>
              <h2 className="adminproducts">Manage Variants</h2>
            </div>
            <div className="header-actions">
              <div className="search-container">
                <span className="search-label">Product:</span>
                <select
                  id="productSelect"
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                  className="user-search-input"
                  style={{ fontWeight: "bold" }}
                >
                  <option value="">-- Choose a Product --</option>
                  {products.map((p) => (
                    <option key={p.id || p._id} value={p.id || p._id}>
                      {p.p_name}
                    </option>
                  ))}
                </select>
              </div>
              {selectedProductId && canWrite && (
                <button className="btn-add-product" onClick={() => { setEditingVariant(null); setShowForm(true); }}>
                  + Add Variant
                </button>
              )}
            </div>
          </div>

          <div className="variant-display-section">
            {selectedProductId ? (
              <>
                <VariantTable
                  variants={variants}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  canWrite={canWrite}
                />

                {groupedProducts.length > 0 && (
                  <div className="grouped-products-section">
                    <h3 className="section-title">Grouped / Related Products</h3>
                    <div className="grouped-products-grid">
                      {groupedProducts.map((gp, idx) => (
                        <div key={idx} className="grouped-product-card" onClick={() => setSelectedProductId(gp.id || gp.productIds?.[0])}>
                          <div className="gp-image">
                            {gp.productImages?.[0]?.[0] ? (
                              <img src={gp.productImages[0][0]} alt={gp.p_name} />
                            ) : (
                              <div className="placeholder-img">📦</div>
                            )}
                          </div>
                          <div className="gp-info">
                            <h4>{gp.p_name}</h4>
                            <p>{gp.totalProducts} Products</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="no-selection-placeholder">
                <div className="placeholder-icon"></div>
                <p>📋 Select a product from the dropdown above to manage its variants.</p>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="page-header">
            <div className="header-with-back">
              <button className="back-btn" onClick={cancelEdit}>← Back</button>
              <h2 className="adminproducts">
                {editingVariant ? "Update Variant" : "Create New Variant"}
                {productName && <span style={{ color: '#666', fontSize: '1rem' }}> : {productName}</span>}
              </h2>
            </div>
          </div>
          <div className="form-inline-container">
            <VariantForm
              productId={selectedProductId}
              productUrl={productUrl}
              productName={productName}
              variant={editingVariant}
              onSuccess={() => {
                if (location.state?.fromDashboard) {
                  navigate("/adminDashboard");
                } else {
                  setEditingVariant(null);
                  setShowForm(false);
                  fetchVariants(selectedProductId);
                }
              }}
              onCancel={cancelEdit}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default VariantPage;

